/**
 * Scopes requested for app installs after this date (includes analytics scopes).
 *
 * @author Ben 2024-07-11
 */
const SHOPIFY_API_SCOPES_NORMAL = [
  "read_content",
  "read_files",
  "read_product_listings",
  "read_products",
  "read_script_tags",
  "read_themes",
  "write_content",
  "write_files",
  "write_products",
  "write_script_tags",
  "write_themes",
  // These are the additional ones for analytics.
  "read_customer_events",
  "write_pixels",
  "read_pixels",
  // Used primarily for bundle builder discounting.
  "write_discounts",

  // Admin metaobject access, used by the agent's metaobject tools. The
  // Storefront API can read entries but has no metaobject-definition query at
  // all, so discovering which metaobject types a store has requires Admin.
  "read_metaobject_definitions",
  "read_metaobjects",

  // Unauthenticated scopes for the storefront API, used for
  // product syncing and (in the future) replo hosted pages
  "unauthenticated_read_checkouts",
  "unauthenticated_write_checkouts",
  "unauthenticated_read_metaobjects",
  "unauthenticated_read_product_listings",
  "unauthenticated_read_product_tags",
  "unauthenticated_read_selling_plans",
];

export const serializedScopes = {
  Normal: SHOPIFY_API_SCOPES_NORMAL.sort().join(","),
};

/**
 * Scopes the bundle-configuration sync genuinely needs.
 *
 * Feature gates check their own scopes as a subset of what the merchant
 * granted, rather than comparing against the whole requested set: adding a
 * scope for a new feature must not break merchants who authorized before that
 * scope existed. They keep working and pick the new scope up on reinstall.
 */
export const SHOPIFY_BUNDLE_CONFIGURATION_SCOPES = ["write_discounts"];

/**
 * Shopify leaves a read scope out of a grant that includes its write scope
 * (`write_files` without `read_files`), so a granted write scope also counts
 * as its read scope, the same rule Shopify's API library applies.
 */
export function hasAccessScopes(granted: string, required: string[]): boolean {
  const grantedScopes = new Set(
    granted
      .split(",")
      .map((scope) => scope.trim())
      .flatMap((scope) => [
        scope,
        scope.replace(/^(unauthenticated_)?write_/, "$1read_"),
      ]),
  );
  return required.every((scope) => grantedScopes.has(scope));
}
